import {
  computed,
  type MaybeRefOrGetter,
  onScopeDispose,
  readonly,
  shallowRef,
  toValue,
} from 'vue';

export const CLICK_DURATION_MS = 1000;

export interface UseRepeatedClickCounterOptions {
  count: MaybeRefOrGetter<number>;
  showAfterClicks: MaybeRefOrGetter<number>;
  onStart?: () => void;
  onSuccess?: () => void;
}

export function useRepeatedClickCounter(
  options: UseRepeatedClickCounterOptions
) {
  const clickedCount = shallowRef(0);
  const isHidden = computed(
    () => clickedCount.value < toValue(options.showAfterClicks)
  );
  const isSuccess = computed(
    () => clickedCount.value === toValue(options.count)
  );
  const countScale = computed(() => 0.1 * clickedCount.value + 1);

  const activeTimers = new Set<ReturnType<typeof setTimeout>>();
  let resetTimerId: ReturnType<typeof setTimeout> | null = null;
  let internalClickCount = 0;

  function trackedTimeout(
    callback: () => void,
    delay: number
  ): ReturnType<typeof setTimeout> {
    const id = setTimeout(() => {
      activeTimers.delete(id);
      callback();
    }, delay);
    activeTimers.add(id);
    return id;
  }

  function handleClick(): void {
    const count = toValue(options.count);
    internalClickCount += 1;
    clickedCount.value = Math.min(internalClickCount, count);

    if (internalClickCount === 1) {
      options.onStart?.();
      resetTimerId = trackedTimeout(() => {
        internalClickCount = 0;
        clickedCount.value = 0;
      }, count * CLICK_DURATION_MS);
    }

    if (internalClickCount === count) {
      trackedTimeout(() => {
        if (resetTimerId !== null) {
          clearTimeout(resetTimerId);
          activeTimers.delete(resetTimerId);
          resetTimerId = null;
        }
        internalClickCount = 0;
        clickedCount.value = 0;
        options.onSuccess?.();
      }, CLICK_DURATION_MS);
    }
  }

  onScopeDispose(() => {
    for (const id of activeTimers) clearTimeout(id);
    activeTimers.clear();
  });

  return {
    clickedCount: readonly(clickedCount),
    isHidden,
    isSuccess,
    countScale,
    handleClick,
  };
}
